import { useRef, useState } from "react";
import { Upload, Link as LinkIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { UploadsAPI } from "@/lib/api";
import { toast } from "sonner";

const MAX_VIDEO_MB = 200;

/**
 * VideoInput — URL paste (YouTube/Vimeo/direct) OR device upload (chunked).
 * Calls onChange(url, { uploaded }) — uploaded=true when a file was hosted.
 */
export default function VideoInput({
  value = "",
  onChange,
  placeholder = "https://youtube.com/… ou télécharge une vidéo",
  testId = "video",
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("video/")) {
      toast.error("Sélectionne une vidéo (MP4, WEBM, MOV).");
      return;
    }
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error(`Vidéo trop lourde (max ${MAX_VIDEO_MB} MB).`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { url } = await UploadsAPI.video(file, setProgress);
      onChange?.(url, { uploaded: true });
      toast.success("Vidéo hébergée avec succès");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'upload vidéo");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`${testId}-root`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <LinkIcon className="w-3 h-3 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value, { uploaded: false })}
            placeholder={placeholder}
            data-testid={`${testId}-url`}
            className="bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 text-xs pl-8 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37]"
          />
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload-btn`}
          className="inline-flex items-center gap-1.5 shrink-0 text-[10px] uppercase tracking-[0.18em] font-bold border border-white/15 bg-white/[0.04] text-zinc-300 hover:text-white hover:bg-white/[0.08] rounded-md px-3 py-2.5 disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin text-[#D4AF37]" />
              <span className="tabular-nums">{progress}%</span>
            </>
          ) : (
            <>
              <Upload className="w-3 h-3" /> Vidéo
            </>
          )}
        </button>
      </div>
      {uploading && (
        <div className="h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, background: "#D4AF37" }}
          />
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f);
          e.target.value = "";
        }}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />
    </div>
  );
}
